import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, Upload, FileText, Settings, History, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import FileUploadMapping from '@/components/mapping/FileUploadMapping';
import MappingTable, { MappingConfiguration } from '@/components/mapping/MappingTable';
import VendorSelect from '@/components/VendorSelect';

import { WorkflowProgress, ProductFeedHistory } from '../product-feed-management-2/components';
import {
    Step1Completion,
    Step2Completion,
} from '../product-feed-management-2/steps';
import { FileProcessingResponse } from '@/services/spreadsheetService';
import { getProductFeedPresignedUrl, uploadFileWithPresignedUrl, submitProductFeedAttributes, processNextStep, fetchFileProcessStatus, fetchProductFileProcessingId, fetchProductFeedProcessingDetails, fetchProductFeedAttributes, fetchVendors, publishToAssistant } from '@/services/api';
import { toast } from 'sonner';
import { Vendor, ImageConfig, VendorStorageLocationType } from '@/types';
import React from 'react';
import { ProductFeedProcessingData, TaskStatus } from '@/types/productFeed';
import { workFlowStepsConstants, workFlowStepsConstantsReverse } from '@/lib/constants/workFlowStepsConstants';
import { validateImageConfig } from '@/lib/validators/imageConfigValidator';
import { MultiSelect } from '@/components/MultiSelect';
import { normalize, stripQueryParams } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import StepContainer from '../product-feed-management-2/components/StepContainer';
import { Image, Rocket } from 'lucide-react';
import { WorkflowStep } from '../product-feed-management-2/components/WorkflowProgress';
import PublishToAssistant from './components/PublishToAssistant';

interface FeedStats {
    total: number;
    completed: number;
    inProgress: number;
    pending: number;
    failed: number;
}

interface FeedRecord {
    _id: string;
    id: number;
    opType: string;
    vendorName: string;
    rawProductFeedLocation: string;
    rawProductFeedArchiveLocation: string;
    rawProductFeedValidationReportLocation: string;
    status: string;
    collectionName: string;
    created_at: string;
    updated_at?: string;
    taskStatus?: Array<{
        bucket?: string;
        key?: string;
        collectionName?: string;
        status: string;
        task: string;
        processedAt?: string;
        rawProductFeedValidationReportLocation?: string;
    }>;
}

/**
 * ProductFeedManagement2 Component
 * 
 * Enhanced version with improved UI design based on Figma mockup
 * Handles the complete product feed upload and mapping process:
 * 1. Dashboard overview with statistics and filtering
 * 2. Workflow progress tracking (9 steps)
 * 3. Vendor selection
 * 4. File upload and processing
 * 5. Attribute mapping configuration
 * 6. File upload to presigned URL
 * 7. Mapping configuration submission
 */
const ProductFeedManagement2 = () => {
    let role = "admin"
    let supplierId = "aamerica"
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const processIdParam = params.get('processId');
    const stepParam = params.get('step');
    const vendorParam = params.get('vendor');
    const isValidStep = stepParam ? !isNaN(parseInt(stepParam)) && parseInt(stepParam) >= 0 && parseInt(stepParam) <= 9 : false;
    const [workflowStep, setWorkflowStep] = useState(isValidStep ? parseInt(stepParam) : 1);
    const [showLoader, setShowLoader] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [supplierHeaders, setSupplierHeaders] = useState<string[]>([]);
    const [showMappingTable, setShowMappingTable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
    const [activeTab, setActiveTab] = useState('upload');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentStepText, setCurrentStepText] = useState<string>('');
    const [step1Completion, setStep1Completion] = useState<boolean>(false);
    const [step2Completion, setStep2Completion] = useState<boolean>(false);
    const [processId, setProcessId] = useState<number | null>(parseInt(processIdParam) ?? null);
    const [productFeedProcessingData, setProductFeedProcessingData] = useState<TaskStatus[] | null>(null);
    const [productFeedProcessingDataObj, setProductFeedProcessingDataObj] = useState<ProductFeedProcessingData | null>(null);

    // Image Processing Configuration State
    const [vendorStorageLocationType, setVendorStorageLocationType] = useState<VendorStorageLocationType>('Hosted Images');
    const [nestingaleS3Location, setNestingaleS3Location] = useState<string>(`s3://vendor-sftp-storage/${selectedVendor?.vendorName || ""}`);
    const [imageNamingSyntax, setImageNamingSyntax] = useState<string>('SKU_');
    const [dropboxFolderStructureType, setDropboxFolderStructureType] = useState<string>('Folder Structure By SKU');
    const [columnName, setColumnName] = useState<string[]>([]);
    const [columnNameOptions, setColumnNameOptions] = useState<string[]>([]);
    const [sharedFolderUrl, setSharedFolderUrl] = useState<string>('');
    const [searchImagesPattern, setSearchImagesPattern] = useState<string>('skuname*');


    // Update Nestingale S3 Location when vendor changes
    React.useEffect(() => {
        if (selectedVendor) {
            setNestingaleS3Location(`s3://vendor-sftp-storage/${selectedVendor?.vendorName || ""}`);
        }
    }, [selectedVendor]);

    useEffect(() => {
        // to poll for status updates every 15 seconds if processId is available
        let intervalId: NodeJS.Timeout;
        if (processId && (workflowStep === 0 || step1Completion || step2Completion)) {
            intervalId = setInterval(async () => {
                try {
                    const productFeedProcessingDetails = await fetchProductFeedProcessingDetails(processId, 'SupplierProductFeed');
                    if (!productFeedProcessingDetails || !productFeedProcessingDetails?.data || productFeedProcessingDetails?.data?.length === 0) {
                        throw new Error('No processing details found for the given process ID');
                    }
                    let taskStatus: any;
                    let productFeed: ProductFeedProcessingData | undefined;
                    if (productFeedProcessingDetails?.data?.length > 1) {
                        setProductFeedProcessingDataObj(productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId));
                        taskStatus = productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId)[0]?.taskStatus || [];
                    } else {
                        productFeed = productFeedProcessingDetails?.data?.[0];
                        if (productFeed?.opType !== "SupplierProductFeed" || productFeed?.id !== processId) throw new Error('No matching processing details found for the given process ID and vendor');
                        setProductFeedProcessingDataObj(productFeed)
                        taskStatus = productFeed?.taskStatus || [];
                    }
                    console.log('Product Feed Processing Details:', productFeedProcessingDetails);
                    console.log('Task Status:', taskStatus);
                    let currentTask: TaskStatus | undefined;
                    if (productFeed?.status === "PublishedToAssistant") {
                        setWorkflowStep(3);
                        const params = new URLSearchParams(search);
                        params.set('step', "3");
                        params.set("vendor", productFeedProcessingDetails?.data?.[0]?.vendorName || '');
                        navigate(`${pathname}?${params.toString()}`, { replace: true });
                        clearInterval(intervalId);
                        setProductFeedProcessingData(taskStatus);
                        return
                    }
                    if (workflowStep === 0) {
                        currentTask = taskStatus[taskStatus.length - 1];
                        if (!currentTask || currentTask?.subTasks?.length === 0) {
                            throw new Error('No task status found');
                        }
                        setWorkflowStep(workFlowStepsConstantsReverse[currentTask.task] || 1);
                        const params = new URLSearchParams(search);
                        params.set('step', workFlowStepsConstantsReverse[currentTask.task]?.toString() || '1');
                        params.set("vendor", productFeedProcessingDetails?.data?.[0]?.vendorName || '');
                        navigate(`${pathname}?${params.toString()}`, { replace: true });
                        if (workFlowStepsConstantsReverse[currentTask.task] === 1) {
                            setStep1Completion(true);
                        } else if (workFlowStepsConstantsReverse[currentTask.task] === 2) {
                            setStep2Completion(true);
                        }
                    } else {
                        currentTask = taskStatus.find((eachTask: TaskStatus) => eachTask.task === workFlowStepsConstants?.[`step${workflowStep}`]);
                    }
                    if (!currentTask || currentTask?.subTasks?.length === 0) {
                        throw new Error('No task status found for UploadMapValidate');
                    }
                    const allSubTasksCompleted = currentTask?.subTasks?.every((subTask: { status: string; }) => subTask?.status?.toLowerCase() === 'completed');
                    if (allSubTasksCompleted || currentTask?.task === workFlowStepsConstants.step5) {
                        clearInterval(intervalId);
                    }
                    setProductFeedProcessingData(taskStatus);
                } catch (error) {
                    // clearInterval(intervalId);
                    console.error('Error fetching processing details:', error);
                }
            }, 10000);
        }
        return () => clearInterval(intervalId);
    }, [processId, workflowStep, step1Completion, step2Completion]);

    useEffect(() => {
        // If vendorParam is present in URL, pre-select the vendor
        if (!vendorParam || vendorParam?.trim()?.length === 0 || selectedVendor) return;
        const loadVendors = async () => {
            try {
                setShowLoader(true);
                setError(null);
                const vendorsData = await fetchVendors();

                if (vendorsData && Array.isArray(vendorsData)) {
                    const selectedVendor = vendorsData.find(vendor => vendor.vendorName === vendorParam) || null;
                    setSelectedVendor(selectedVendor);
                } else {
                    console.error('Invalid vendor data structure:', vendorsData);
                    setError('Invalid vendor data received. Please try again later.');
                }
            } catch (err) {
                setError('Failed to load vendors. Please try again later.');
                console.error('Error loading vendors:', err);
            } finally {
                setShowLoader(false);
            }
        };

        loadVendors();
    }, [vendorParam, selectedVendor]);

    useEffect(() => {
        const loadSupplierAttributesForDropBox = async () => {
            // Fetch product feed attributes for column mapping options
            try {
                if (selectedVendor?.vendorName && vendorStorageLocationType === 'Dropbox') {
                    const productFeedAttributes = await fetchProductFeedAttributes(selectedVendor.vendorName);
                    console.log('Fetched product feed attributes:', productFeedAttributes);
                    if (productFeedAttributes && productFeedAttributes.nestingaleMap) {
                        const attributes = productFeedAttributes.nestingaleMap;
                        const availableColumns = Object.values(attributes);
                        console.log('Available columns for mapping:', availableColumns);
                        setColumnNameOptions([...new Set(availableColumns.flat(Infinity) as string[])]);
                    }
                }
            } catch (error) {
                console.error('Error fetching product feed attributes:', error);
                toast.error('Failed to fetch product feed attributes. Please try again.');
            }
        }
        loadSupplierAttributesForDropBox();
    }, [selectedVendor?.vendorName, vendorStorageLocationType])

    const handleVendorSelect = (vendor: Vendor | null) => {
        setSelectedVendor(vendor);
    };

    const handleFileProcessed = (data: FileProcessingResponse & { file: File }) => {
        console.log('File processed:', data);
        setSupplierHeaders(data.headers);
        setUploadedFile(data.file);
        if (role === "admin") {
            setShowMappingTable(true);
        } else {
            handleMappingSubmit(null, true, data.file)
        }
    };

    const handleNextStep = async () => {
        if (workflowStep < 3) {
            // If we're on step 2 (Process Images), submit the image configuration
            if (workflowStep === 2) {
                try {
                    setIsSubmitting(true);
                    setCurrentStepText('Submitting image configuration...');
                    setUploadProgress(10);
                    // Build image configuration
                    let imageConfig: ImageConfig = {
                        vendorStorageLocation: vendorStorageLocationType === 'Hosted Images' ? 'hosted' :
                            vendorStorageLocationType === 'Dropbox' ? 'dropbox' :
                                vendorStorageLocationType === 'Google Drive' ? 'googleDrive' : 'hosted',
                        nestingaleLocation: nestingaleS3Location,
                        imgNameSyntax: imageNamingSyntax === 'SKU_' ? 'SKU_Index' : 'saveAsIs',
                    };
                    if (vendorStorageLocationType === 'Dropbox') {
                        imageConfig.dropBox = {
                            type: dropboxFolderStructureType === 'Folder Structure By SKU' ? 'bySku' : 'shared',
                            ...(dropboxFolderStructureType === 'Folder Structure By SKU' && columnName[0] && {
                                folderColumnName: columnName[0],
                            }),
                            ...(dropboxFolderStructureType === 'Folder Structure By SKU' && searchImagesPattern && {
                                imagePattern: searchImagesPattern === 'skuname*' ? 'skuname_*' : '*_skuname'
                            }),
                            ...(dropboxFolderStructureType === 'Shared Folder' && sharedFolderUrl && {
                                sharedFolderURL: sharedFolderUrl
                            }),
                        }
                    }
                    const imageConfigErrors = validateImageConfig(imageConfig, vendorStorageLocationType, selectedVendor || undefined);
                    if (imageConfigErrors.length > 0) {
                        console.error('Image configuration validation errors found:', imageConfigErrors);
                        toast.error('Image configuration validation failed: ' + imageConfigErrors.join('; '));
                        throw new Error('Image configuration validation failed: ' + imageConfigErrors.join('; '));
                    }
                    console.log('=== SUBMITTING IMAGE CONFIGURATION FOR STEP 2 ===');
                    console.log('Vendor Name:', selectedVendor?.vendorName);
                    console.log('Image config:', JSON.stringify(imageConfig, null, 2));

                    // Validate required data
                    if (!selectedVendor?.vendorName) {
                        throw new Error('No vendor selected. Please select a vendor first.');
                    }

                    // Get the processing ID from the file process status API
                    console.log('=== FETCHING PROCESSING ID FROM FILE PROCESS STATUS FOR STEP 2 ===');
                    console.log('Vendor:', selectedVendor.vendorName);

                    try {
                        let processingId = processId; // 👈 use from state first
                        if (!processingId) {
                            // Only fetch if not already available
                            const fileProcessResponse = await fetchFileProcessStatus(
                                selectedVendor.vendorName,
                                "SupplierProductFeed"
                            );
                            console.log("File process status API response:", fileProcessResponse);

                            if (
                                fileProcessResponse &&
                                fileProcessResponse.data &&
                                Array.isArray(fileProcessResponse.data)
                            ) {
                                // Log all available vendor names for debugging
                                const allVendorNames = [
                                    ...new Set(fileProcessResponse.data.map((record: any) => record.vendorName)),
                                ];
                                console.log("Available vendor names in file process status:", allVendorNames);

                                // Try exact match first
                                let vendorRecords = fileProcessResponse.data.filter(
                                    (record: any) =>
                                        record.vendorName.toLowerCase() ===
                                        selectedVendor.vendorName.toLowerCase()
                                );

                                // If no exact match, try with display name
                                if (vendorRecords.length === 0 && selectedVendor.vendorDisplayName) {
                                    vendorRecords = fileProcessResponse.data.filter(
                                        (record: any) =>
                                            record.vendorName.toLowerCase() ===
                                            selectedVendor.vendorDisplayName.toLowerCase()
                                    );
                                    console.log("Trying with display name:", selectedVendor.vendorDisplayName);
                                }

                                console.log("Vendor records:", vendorRecords);

                                if (vendorRecords.length > 0) {
                                    // Get the most recent record (assuming it's sorted by date)
                                    const latestRecord = vendorRecords[0];
                                    console.log("Latest record:", latestRecord);

                                    if (latestRecord.id) {
                                        if (typeof latestRecord.id === "number") {
                                            processingId = latestRecord.id;
                                            console.log("Using direct numeric ID:", processingId);
                                        } else if (typeof latestRecord.id === "string") {
                                            const idMatch = latestRecord.id.match(/ProductPrice_(\d+)/);
                                            if (idMatch) {
                                                processingId = parseInt(idMatch[1], 10);
                                                console.log("Extracted numeric ID:", processingId);
                                            } else {
                                                console.warn("Could not parse processing ID from:", latestRecord.id);
                                            }
                                        }
                                    }
                                } else {
                                    console.error("No records found for vendor:", selectedVendor.vendorName);
                                    toast.error("No file process records found for this vendor. Please try again.");
                                    throw new Error("No file process records found for vendor");
                                }
                            } else {
                                console.error("Invalid file process status response:", fileProcessResponse);
                                toast.error("Invalid file process status response. Please try again.");
                                throw new Error("Invalid file process status response");
                            }
                        }
                        if (processingId) {
                            console.log("Using processing ID:", processingId);
                            setProcessId(processingId); // Save to state for future use
                            // Call the API to process the next step with image configuration
                            await processNextStep(workFlowStepsConstants.step2, processingId, imageConfig);
                            await new Promise((resolve) => setTimeout(resolve, 5000));
                            setUploadProgress(50);
                            setCurrentStepText('Image configuration submitted. Resolving processing ID...');
                            const productFeedProcessingDetails = await fetchProductFeedProcessingDetails(processingId, "SupplierProductFeed");
                            setUploadProgress(70);
                            setCurrentStepText('Fetched Product Feed Processing Details');
                            if (!productFeedProcessingDetails || !productFeedProcessingDetails?.data || productFeedProcessingDetails?.data?.length === 0) {
                                throw new Error('No processing details found for the given process ID');
                            }
                            let taskStatus: any;
                            if (productFeedProcessingDetails?.data?.length > 1) {
                                setProductFeedProcessingDataObj(productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId));
                                taskStatus = productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId)[0]?.taskStatus || [];
                            } else {
                                const productFeed = productFeedProcessingDetails?.data?.[0];
                                if (productFeed?.opType !== "SupplierProductFeed" || productFeed?.id !== processingId && productFeed?.vendorName !== selectedVendor?.vendorName) throw new Error('No matching processing details found for the given process ID and vendor');
                                setProductFeedProcessingDataObj(productFeed)
                                taskStatus = productFeed?.taskStatus || [];
                            }
                            console.log('Product Feed Processing Details:', productFeedProcessingDetails);
                            console.log('Task Status:', taskStatus);

                            const currentTask = taskStatus.find((eachTask: TaskStatus) => eachTask.task === workFlowStepsConstants.step2);
                            if (!currentTask || currentTask?.subTasks?.length === 0) {
                                throw new Error('No task status found for RawFeedImgProcess');
                            }

                            setProductFeedProcessingData(taskStatus);
                        } else {
                            console.error("No valid processing ID available");
                            toast.error("No valid processing ID found. Please try again.");
                            throw new Error("No valid processing ID found");
                        }
                    } catch (error) {
                        console.error("Error resolving processing ID:", error);
                        toast.error("Failed to resolve processing ID. Please try again.");
                        throw error;
                    }

                    setCurrentStepText('Image configuration submitted successfully!');
                    toast.success('Image configuration submitted successfully!');
                    setUploadProgress(100);
                    // Show step 2 completion screen instead of moving to step 3
                    setStep2Completion(true);
                    setCurrentStepText('');

                } catch (error) {
                    console.error('Image configuration submission failed:', error);
                    toast.error('Failed to submit image configuration. Please try again.');
                    setCurrentStepText('Error occurred');
                } finally {
                    setIsSubmitting(false);
                }

            } else {
                // For other steps, just increment
                setWorkflowStep(workflowStep + 1);
                const params = new URLSearchParams(search);
                params.set('step', (workflowStep + 1).toString());
                navigate(`${pathname}?${params.toString()}`, { replace: true });
            }
        }
    };

    const handlePreviousStep = () => {
        if (workflowStep > 1) {
            setWorkflowStep(workflowStep - 1);
            const params = new URLSearchParams(search);
            params.set('step', (workflowStep - 1).toString());
            navigate(`${pathname}?${params.toString()}`, { replace: true });
        }
    };

    const handleMappingSubmit = async (mappings?: MappingConfiguration[], skipMapping = false, fileOverride?: File) => {
        const fileToUse = fileOverride || uploadedFile;
        if (!selectedVendor || !fileToUse) {
            toast.error('Missing vendor or file information');
            return;
        }
        try {
            setIsSubmitting(true);
            setCurrentStepText('Getting presigned URL for file upload...');

            // Step 1: Get presigned URL for file upload
            console.log('🔄 Step 1.1: Getting presigned URL for file upload...');

            const presignedUrlResponse = await getProductFeedPresignedUrl(
                fileToUse.name,
                selectedVendor.vendorName,
                true, // templateRequired
                "",
                'SupplierProductFeed'
            );

            console.log('✅ Step 1.1: Presigned URL obtained successfully');

            setCurrentStepText('Uploading file to S3...');
            setUploadProgress(25);

            // Step 2: Upload the file to the presigned URL
            console.log('🔄 Step 1.2: Uploading file to S3...');
            await uploadFileWithPresignedUrl(
                presignedUrlResponse.preSignedUrl,
                fileToUse,
                fileToUse.name,
                selectedVendor.vendorName
            );

            console.log('✅ Step 1.2: File uploaded to S3 successfully');

            setUploadProgress(50);
            setCurrentStepText('Submitting mapping configuration...');

            // Step 3: Transform mappings to the expected API format
            console.log('🔄 Step 1.3: Submitting mapping configuration...');
            if (!skipMapping) {
                const nestingaleMap: Record<string, string | string[]> = {};


                // Function to normalize attribute names to match API expectations
                const normalizeAttributeName = (attr: string): string => {
                    // Map common variations to the expected API format based on actual API response
                    const attributeMap: Record<string, string> = {
                        'productSkuID': 'productSkuID', // API uses capital ID
                        'productSkuId': 'productSkuID', // Fix casing for SKU ID
                        'SKU': 'productSkuID',
                        'sku': 'productSkuID',
                        'imageLink': 'imageLink',
                        'additionalImageLink': 'additionalImageLink',
                        'colorOriginal': 'colorOriginal',
                        'productLength': 'productLength',
                        'productHeight': 'productHeight',
                        'productWeight': 'productWeight',
                        'productWidth': 'productWidth',
                        'features': 'features',
                        'materialOriginal': 'materialOriginal',
                        'mapPrice': 'mapPrice',
                        'wholesalePrice': 'wholesalePrice',
                        'numberOfCartons': 'numberOfCartons',
                        'title': 'title',
                        'description': 'description',
                        'productDetails': 'productDetails',
                        'styleDisplayName': 'styleDisplayName'
                    };

                    return attributeMap[attr] || attr;
                };

                // Include all attributes that the API expects based on the actual response
                const expectedAttributes = [
                    'productSkuID', 'imageLink', 'additionalImageLink', 'colorOriginal',
                    'productLength', 'productHeight', 'productWeight', 'productWidth',
                    'features', 'materialOriginal', 'mapPrice', 'wholesalePrice',
                    'numberOfCartons', 'title', 'description', 'productDetails',
                    'styleDisplayName'
                ];

                mappings.forEach(mapping => {
                    const normalizedAttribute = normalizeAttributeName(mapping.nestingaleAttribute);

                    // Only include if it's an expected attribute
                    // if (expectedAttributes.includes(normalizedAttribute)) {
                    if (mapping.supplierAttributes.length === 1) {
                        // Single attribute maps to a string
                        nestingaleMap[normalizedAttribute] = mapping.supplierAttributes[0];
                    } else {
                        // Multiple attributes map to an array
                        nestingaleMap[normalizedAttribute] = mapping.supplierAttributes;
                    }
                    // }
                });

                await submitProductFeedAttributes(selectedVendor.vendorName, nestingaleMap);
            }

            console.log('✅ Step 1.3: Mapping configuration submitted successfully');

            setUploadProgress(70);
            setCurrentStepText('Step 1 completed successfully!');
            console.log('🎉 Step 1: File Upload & Mapping completed successfully!');
            toast.success('File uploaded and mapping configuration submitted successfully!');

            const { processId } = await fetchProductFileProcessingId(stripQueryParams(presignedUrlResponse.preSignedUrl), selectedVendor.vendorName, fileToUse.name);
            console.log('Fetched Product Feed Processing ID:', processId);
            if (processId) {
                setProcessId(processId)
                const params = new URLSearchParams(search);
                params.set('processId', processId.toString());
                navigate(`${pathname}?${params.toString()}`, { replace: true });
            }
            setCurrentStepText('Fetched Product Feed Processing ID');
            setUploadProgress(80)
                ;
            const productFeedProcessingDetails = await fetchProductFeedProcessingDetails(processId, "SupplierProductFeed");
            setCurrentStepText('Fetched Product Feed Processing Details');
            setUploadProgress(90);
            if (!productFeedProcessingDetails || !productFeedProcessingDetails?.data || productFeedProcessingDetails?.data?.length === 0) {
                throw new Error('No processing details found for the given process ID');
            }

            let taskStatus: any;
            if (productFeedProcessingDetails?.data?.length > 1) {
                setProductFeedProcessingDataObj(productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId));
                taskStatus = productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId)[0]?.taskStatus || [];
            } else {
                const productFeed = productFeedProcessingDetails?.data?.[0];
                if (productFeed?.opType !== "SupplierProductFeed" || productFeed?.id !== processId && productFeed?.vendorName !== selectedVendor?.vendorName) throw new Error('No matching processing details found for the given process ID and vendor');
                setProductFeedProcessingDataObj(productFeed)
                taskStatus = productFeed?.taskStatus || [];
            }
            console.log('Product Feed Processing Details:', productFeedProcessingDetails);
            console.log('Task Status:', taskStatus);

            const currentTask = taskStatus.find((eachTask: TaskStatus) => eachTask.task === workFlowStepsConstants.step1);
            if (!currentTask || currentTask?.subTasks?.length === 0) {
                throw new Error('No task status found for UploadMapValidate');
            }

            setProductFeedProcessingData(taskStatus);
            setUploadProgress(100);
            // Show step 1 completion screen instead of moving to step 2
            setStep1Completion(true);
            setCurrentStepText('');

        } catch (error) {
            console.error('❌ Step 1 failed:', error);

            // Provide more specific error messages
            let errorMessage = 'Failed to complete step 1';
            if (error instanceof Error) {
                if (error.message.includes('SignatureDoesNotMatch')) {
                    errorMessage = 'File upload failed due to signature mismatch. Please try again.';
                } else if (error.message.includes('403')) {
                    errorMessage = 'File upload failed due to permission issues. Please check your access.';
                } else if (error.message.includes('404')) {
                    errorMessage = 'File upload failed - URL not found. Please try again.';
                } else {
                    errorMessage = error.message;
                }
            }

            toast.error(errorMessage);
            setCurrentStepText('Error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleProcessImages = async () => {
        // Move to step 2 (Process Images)
        setWorkflowStep(2);
        const params = new URLSearchParams(search);
        params.set('step', '2');
        navigate(`${pathname}?${params.toString()}`, { replace: true });
        setStep1Completion(false);
    };

    const handleTabChange = (value: string) => {
        setActiveTab(value);
    };
    const handleProcessStep = async (
        stepNumber: number,
        successMessage: string,
        errorMessage: string,
        shouldReprocess: boolean = false
    ) => {
        try {
            setIsSubmitting(true);
            const params = new URLSearchParams(search);

            // ✅ Check if this step is already present
            const isStepPresent = productFeedProcessingDataObj.status === "PublishedToAssistant"
            if (isStepPresent && !shouldReprocess) {
                setWorkflowStep(stepNumber);
                params.set('step', String(stepNumber));
                navigate(`${pathname}?${params.toString()}`, { replace: true });
                setIsSubmitting(false);
                return;
            }

            setUploadProgress(0);

            // ✅ Start processing
            await publishToAssistant(processId);
            setUploadProgress(30);
            setCurrentStepText(`${successMessage}`);
            toast.success(`${successMessage}`);

            // Wait for backend to update before fetching details
            await new Promise((resolve) => setTimeout(resolve, 5000));

            // ✅ Fetch details
            const productFeedProcessingDetails = await fetchProductFeedProcessingDetails(processId, "SupplierProductFeed");
            setCurrentStepText('Fetched Product Feed Processing Details');
            setUploadProgress(60);

            if (
                !productFeedProcessingDetails ||
                !productFeedProcessingDetails?.data ||
                productFeedProcessingDetails?.data?.length === 0
            ) {
                throw new Error('No processing details found for the given process ID');
            }

            // ✅ Extract relevant task status
            let taskStatus: any;
            let productFeed: any;
            if (productFeedProcessingDetails?.data?.length > 1) {
                setProductFeedProcessingDataObj(productFeedProcessingDetails?.data?.filter((item: ProductFeedProcessingData) => item.opType === "SupplierProductFeed" && item.vendorName === selectedVendor?.vendorName && item.id === processId));
                taskStatus =
                    productFeedProcessingDetails?.data?.filter(
                        (item: ProductFeedProcessingData) =>
                            item.opType === 'SupplierProductFeed' &&
                            item.vendorName === selectedVendor.vendorName &&
                            item.id === parseInt(processIdParam)
                    )[0]?.taskStatus || [];
            } else {
                productFeed = productFeedProcessingDetails?.data?.[0];
                if (
                    productFeed?.opType !== 'SupplierProductFeed' ||
                    (productFeed?.id !== parseInt(processIdParam) &&
                        productFeed?.vendorName !== selectedVendor?.vendorName)
                )
                    throw new Error(
                        'No matching processing details found for the given process ID and vendor'
                    );
                setProductFeedProcessingDataObj(productFeed)
                taskStatus = productFeed?.taskStatus || [];
            }

            console.log('Product Feed Processing Details:', productFeedProcessingDetails);
            console.log('Task Status:', taskStatus);

            if (productFeed?.status !== "PublishedToAssistant") {
                throw new Error("Product Feed is not published to assistant");
            }


            // ✅ Success flow
            setUploadProgress(100);
            setProductFeedProcessingData(taskStatus);
            setCurrentStepText(`Processing ${successMessage.toLowerCase()}...`);
            setWorkflowStep(stepNumber);
            params.set('step', String(stepNumber));
            navigate(`${pathname}?${params.toString()}`, { replace: true });
            setIsSubmitting(false);
            setCurrentStepText('');
        } catch (error) {
            console.error(`Error during ${errorMessage}:`, error);
            setIsSubmitting(false);
            setCurrentStepText('');
            toast.error(`Failed to process ${errorMessage}. Please try again.`);
        }
    };


    const workflowSteps: WorkflowStep[] = [
        {
            id: 1,
            title: 'Upload, Map & Validate',
            icon: Upload
        },
        {
            id: 2,
            title: 'Process Images',
            icon: Image
        },
        {
            id: 3,
            title: 'Publish to Assistant',
            icon: Rocket
        }
    ];

    const stepConfigs = [
        {
            step: 3,
            component: (
                <PublishToAssistant
                    vendor={selectedVendor?.vendorDisplayName || 'Unknown Vendor'}
                    onGoToProducts={() => { navigate('/ai/products') }}
                    status={productFeedProcessingDataObj?.status || "pending"}
                />
            ),
        },
    ];

    // Workflow view
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Section */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Product Feed Workflow</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectedVendor && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {selectedVendor?.vendorDisplayName}
                            </Badge>
                        )}
                        <div className="flex items-center gap-2">
                            {processIdParam && <Button
                                variant="outline"
                                onClick={() => {
                                    setWorkflowStep(0);
                                    const params = new URLSearchParams(search);
                                    params.set('step', (0).toString());
                                    navigate(`${pathname}?${params.toString()}`, { replace: true });
                                }}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                Refresh
                            </Button>}
                            {(productFeedProcessingData?.some(task => task?.task === workFlowStepsConstants[`step${workflowStep}`]) || workflowStep === 3) && <Button
                                variant="outline"
                                onClick={() => {
                                    if (isSubmitting) return;
                                    if (stepParam === "0") {
                                        toast.error('Cannot re-process while in initial loading state. Please refresh first.');
                                        return;
                                    }
                                    if (stepParam === "1") {
                                        setStep1Completion(false);
                                        return
                                    }
                                    if (stepParam === "2") {
                                        setStep2Completion(false);
                                        return
                                    }
                                    if (stepParam === "3") {
                                        handleProcessStep(3, 'Publish to Assistant initiated successfully', 'Publish to Assistant', true);
                                        return
                                    }
                                }}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                Re Process
                            </Button>}
                        </div>

                    </div>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Workflow Progress */}
                <WorkflowProgress workflowSteps={workflowSteps} currentStep={workflowStep} className="mb-8" completedStepIds={productFeedProcessingData?.map(task => workFlowStepsConstantsReverse[normalize(task?.task)])} onClickStep={(stepId, event) => {
                    event.stopPropagation();
                    if (!processIdParam || stepParam === "0") return;
                    else {
                        if (stepId === 1) {
                            const isStep1Completed = productFeedProcessingData?.some(task => task?.task === workFlowStepsConstants.step1);
                            if (isStep1Completed) {
                                setStep1Completion(true);
                            } else {
                                setStep1Completion(false);
                            }
                        } else if (stepId === 2) {
                            const isStep2Completed = productFeedProcessingData?.some(task => task?.task === workFlowStepsConstants.step2);
                            if (isStep2Completed) {
                                setStep2Completion(true);
                            } else {
                                setStep2Completion(false);
                            }
                        } else if (stepId === 3) {
                            return;
                        }
                        setWorkflowStep(stepId);
                        const params = new URLSearchParams(search);
                        params.set('step', stepId.toString());
                        navigate(`${pathname}?${params.toString()}`, { replace: true });
                    }
                }} />

                {/* Loading Indicator */}
                {(workflowStep === 0 || showLoader) && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                )}
                {/* Step Content */}
                {workflowStep === 1 && (
                    <div className="space-y-6">
                        {step1Completion ? (
                            // Show step 1 completion screen
                            <Step1Completion
                                vendor={selectedVendor?.vendorDisplayName || 'Unknown Vendor'}
                                fileName={uploadedFile?.name || 'No file uploaded'}
                                fileSize={uploadedFile ? `${(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB` : '0 MB'}
                                onProcessImages={handleProcessImages}
                                stepOneTasks={productFeedProcessingData?.find(task => task?.task === workFlowStepsConstants.step1)}
                            />
                        ) : (
                            <>
                                <Tabs
                                    value={activeTab}
                                    onValueChange={handleTabChange}
                                    className="w-full"
                                >
                                    <TabsList className="grid w-full grid-cols-2 mb-8 bg-white border border-gray-200">
                                        <TabsTrigger value="upload" className="flex items-center gap-2">
                                            <Upload className="w-4 h-4" />
                                            Upload & Configure
                                        </TabsTrigger>
                                        <TabsTrigger value="history" className="flex items-center gap-2">
                                            <History className="w-4 h-4" />
                                            Processing History
                                        </TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="upload" className="space-y-6">
                                        {!showMappingTable ? (
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                                {/* Left Column - Vendor Selection */}
                                                <div className="lg:col-span-1">
                                                    <Card className="bg-white border border-gray-200 shadow-sm">
                                                        <CardHeader className="pb-4">
                                                            <CardTitle className="flex items-center gap-2 text-lg">
                                                                <Settings className="w-5 h-5 text-blue-600" />
                                                                Vendor Configuration
                                                            </CardTitle>
                                                            <CardDescription>
                                                                Select the vendor for this product feed upload
                                                            </CardDescription>
                                                        </CardHeader>
                                                        <CardContent>
                                                            <VendorSelect onVendorSelect={(data) => {
                                                                setSelectedVendor(data);
                                                                const params = new URLSearchParams(search);
                                                                params.set('vendor', data.vendorName);
                                                                navigate(`${pathname}?${params.toString()}`, { replace: true });
                                                            }} selectedVendor={selectedVendor} preSelectedVendorId={role === "admin" ? null : "1000003"} />
                                                        </CardContent>
                                                    </Card>
                                                </div>

                                                {/* Right Column - File Upload */}
                                                <div className="lg:col-span-2">
                                                    <Card className="bg-white border border-gray-200 shadow-sm">
                                                        <CardHeader className="pb-4">
                                                            <CardTitle className="flex items-center gap-2 text-lg">
                                                                <FileText className="w-5 h-5 text-green-600" />
                                                                File Upload
                                                            </CardTitle>
                                                            <CardDescription>
                                                                Upload your product feed file to begin the mapping process
                                                            </CardDescription>
                                                        </CardHeader>
                                                        <CardContent>
                                                            {selectedVendor ? (
                                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50">
                                                                    <FileUploadMapping onFileProcessed={handleFileProcessed} />
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-12">
                                                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                                        <AlertCircle className="w-8 h-8 text-gray-400" />
                                                                    </div>
                                                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Vendor First</h3>
                                                                    <p className="text-gray-600">Please choose a vendor before uploading a file</p>
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {/* Progress Indicator */}
                                                {isSubmitting && (
                                                    <Card className="bg-white border border-gray-200 shadow-sm">
                                                        <CardContent className="p-6">
                                                            <div className="flex items-center gap-4 mb-4">
                                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h3 className="font-medium text-gray-900">{currentStepText}</h3>
                                                                    <p className="text-sm text-gray-600">Please wait while we process your request</p>
                                                                </div>
                                                            </div>
                                                            <Progress value={uploadProgress} className="w-full" />
                                                        </CardContent>
                                                    </Card>
                                                )}

                                                {/* Mapping Configuration */}
                                                <Card className="bg-white border border-gray-200 shadow-sm">
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center gap-2 text-lg">
                                                            <Settings className="w-5 h-5 text-orange-600" />
                                                            Attribute Mapping Configuration
                                                        </CardTitle>
                                                        <CardDescription>
                                                            Configure how supplier attributes map to Nestingale attributes for {selectedVendor?.vendorDisplayName}
                                                        </CardDescription>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <MappingTable
                                                            supplierHeaders={supplierHeaders}
                                                            onSubmit={handleMappingSubmit}
                                                            vendorName={selectedVendor?.vendorName}
                                                            isSubmitting={isSubmitting}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        )}
                                    </TabsContent>

                                    <TabsContent value="history">
                                        <ProductFeedHistory
                                            selectedVendor={selectedVendor}
                                            onVendorSelect={handleVendorSelect}
                                        />
                                    </TabsContent>
                                </Tabs>
                            </>
                        )}
                    </div>
                )}

                {workflowStep === 2 && (
                    <div className="space-y-6">
                        {isSubmitting && (
                            <Card className="bg-white border border-gray-200 shadow-sm">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-medium text-gray-900">{currentStepText}</h3>
                                            <p className="text-sm text-gray-600">Please wait while we process your request</p>
                                        </div>
                                    </div>
                                    <Progress value={uploadProgress} className="w-full" />
                                </CardContent>
                            </Card>
                        )}
                        {step2Completion ? (
                            // Show step 2 completion screen
                            <Step2Completion
                                vendor={selectedVendor?.vendorDisplayName || 'Unknown Vendor'}
                                vendorStorageLocationType={vendorStorageLocationType}
                                nestingaleS3Location={selectedVendor?.vendorStorageLocation || `s3://nestingale-images/${selectedVendor?.vendorName}`}
                                imageNamingSyntax={imageNamingSyntax}
                                dropboxFolderStructureType={dropboxFolderStructureType}
                                imageColumnName={columnName[0] || 'image_urls'}
                                searchImagesPattern={searchImagesPattern}
                                onPopulateMasterData={async () => {
                                    // if (isSubmitting) return;
                                    // try {
                                    //     setIsSubmitting(true);
                                    //     toast.loading('Publishing to Assistant...');
                                    //     await publishToAssistant(processIdParam)
                                    //     toast.success('Published to Assistant successfully!');
                                    //     setWorkflowStep(3);
                                    //     params.set('step', String(3));
                                    //     navigate(`${pathname}?${params.toString()}`, { replace: true });
                                    //     setIsSubmitting(false);
                                    // } catch (error) {
                                    //     setIsSubmitting(false);
                                    //     console.error('Error publishing to Assistant:', error);
                                    //     toast.error('Error publishing to Assistant!');
                                    // }
                                    handleProcessStep(3, 'Publish to Assistant initiated successfully', 'Publish to Assistant', false);
                                }}
                                stepTwoTasks={productFeedProcessingData?.find(task => task?.task === workFlowStepsConstants.step2)}
                                buttonText={isSubmitting ? "Publishing to Assistant..." : "Publish to Assistant"}
                            />
                        ) : (
                            <>
                                <Card className="bg-white border border-gray-200 shadow-sm">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-lg">
                                            <Settings className="w-5 h-5 text-purple-600" />
                                            Image Processing Configuration
                                        </CardTitle>
                                        <CardDescription>
                                            Configure how images are processed and stored for your product feed
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="space-y-2">
                                                <Label htmlFor="vendorStorageLocationType" className="text-sm font-medium">
                                                    Storage Location Type
                                                </Label>
                                                <Select value={vendorStorageLocationType} onValueChange={(value) => setVendorStorageLocationType(value as VendorStorageLocationType)}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Hosted Images">Hosted Images</SelectItem>
                                                        <SelectItem value="Dropbox">Dropbox</SelectItem>
                                                        <SelectItem value="Google Drive" disabled className='disabled:cursor-not-allowed'>Google Drive</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="nestingaleS3Location" className="text-sm font-medium">
                                                    Nestingale S3 Location
                                                </Label>
                                                <Input
                                                    id="nestingaleS3Location"
                                                    value={nestingaleS3Location}
                                                    onChange={(e) => setNestingaleS3Location(e.target.value)}
                                                    placeholder="s3://vendor-sftp-storage/vendor-name/"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="imageNamingSyntax" className="text-sm font-medium">
                                                    Image Naming Syntax
                                                </Label>
                                                <Select value={imageNamingSyntax} onValueChange={setImageNamingSyntax}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="SKU_">SKU_Index</SelectItem>
                                                        <SelectItem value="saveAsIs">Save As Is</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {vendorStorageLocationType === 'Dropbox' && (
                                                <>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="dropboxFolderStructureType" className="text-sm font-medium">
                                                            Dropbox Folder Structure Type
                                                        </Label>
                                                        <Select value={dropboxFolderStructureType} onValueChange={setDropboxFolderStructureType}>
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="Folder Structure By SKU">Folder Structure By SKU</SelectItem>
                                                                <SelectItem value="Shared Folder">Shared Folder</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    {dropboxFolderStructureType === 'Shared Folder' && <div className="space-y-2">
                                                        <Label htmlFor="sharedFolderUrl" className="text-sm font-medium">
                                                            Shared Folder URL
                                                        </Label>
                                                        <Input
                                                            id="sharedFolderUrl"
                                                            value={sharedFolderUrl}
                                                            onChange={(e) => { setSharedFolderUrl(e.target.value) }}
                                                            placeholder="Enter Dropbox URL"
                                                        />
                                                    </div>}

                                                    {dropboxFolderStructureType === 'Folder Structure By SKU' && <div className="space-y-2 pt-1">
                                                        <MultiSelect
                                                            label="Add supplier attribute"
                                                            options={columnNameOptions}
                                                            value={columnName}
                                                            onChange={setColumnName}
                                                            mode='single'
                                                        />
                                                    </div>}
                                                </>
                                            )}

                                            {(vendorStorageLocationType === 'Dropbox' && dropboxFolderStructureType === "Folder Structure By SKU") && <div className="space-y-2">
                                                <Label htmlFor="searchImagesPattern" className="text-sm font-medium">
                                                    Search Images Pattern
                                                </Label>
                                                <Select value={searchImagesPattern} onValueChange={setSearchImagesPattern}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="skuname*">skuname_*</SelectItem>
                                                        <SelectItem value="*_skuname">*_skuname</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>}
                                        </div>

                                        <div className="flex justify-end mt-6">
                                            <Button
                                                onClick={handleNextStep}
                                                disabled={isSubmitting}
                                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                            >
                                                {isSubmitting ? 'Processing...' : 'Process Images'}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </div>
                )}

                {stepConfigs.map(({ step, component }) => (
                    <StepContainer
                        key={step}
                        workflowStep={workflowStep}
                        currentStep={step}
                        isSubmitting={isSubmitting}
                        currentStepText={currentStepText}
                        uploadProgress={uploadProgress}
                        completionComponent={component}
                    />
                ))}


                {/* Placeholder for other steps */}
                {workflowStep > 3 && (
                    <div className="space-y-6">
                        <Card className="bg-white border border-gray-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <Settings className="w-5 h-5 text-blue-600" />
                                    Step {workflowStep} - Coming Soon
                                </CardTitle>
                                <CardDescription>
                                    This step is currently under development
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Settings className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Step {workflowStep} Not Yet Implemented</h3>
                                    <p className="text-gray-600">This workflow step will be available in a future update</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between">
                            <Button
                                variant="outline"
                                onClick={handlePreviousStep}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Previous Step
                            </Button>
                            {workflowStep < 9 && (
                                <Button
                                    onClick={handleNextStep}
                                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                                >
                                    Next Step
                                    <ArrowRight className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductFeedManagement2; 